import { useState, Fragment, useEffect, useContext, createContext } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { auth, db } from '../firebase'
import { avatarUrl, staffAvatar } from '../lib/avatar'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { Dialog, Transition, Menu } from '@headlessui/react'
import { Icons } from '../lib/Icons'
import { useUserRole, ROLES, hasRole } from '../lib/auth/roles'
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities'
import { useSchoolId } from '../lib/auth/schoolContext'
import { fetchStudentsForSchool, resolveStudentSearch } from '../lib/studentSearch'

const baseNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Icons.Home, capability: SCHOOL_ACTIONS.VIEW_DASHBOARD },
  { name: 'Students', href: '/student-organization', icon: Icons.Users, capability: SCHOOL_ACTIONS.VIEW_STUDENTS },
  { name: 'Individual Support Plans', href: '/isps', icon: Icons.AcademicCap, capability: SCHOOL_ACTIONS.MANAGE_SUPPORT_PLANS },
  { name: 'One-Page Profiles', href: '/one-page-profiles', icon: Icons.BookOpen, capability: SCHOOL_ACTIONS.MANAGE_SUPPORT_PLANS },
  { name: 'Interventions', href: '/interventions', icon: Icons.Target, capability: SCHOOL_ACTIONS.MANAGE_INTERVENTIONS },
  { name: 'Resource Library', href: '/resources', icon: Icons.BookOpen, capability: SCHOOL_ACTIONS.VIEW_STUDENTS },
  { name: 'Progress Dashboard', href: '/progress-dashboard', icon: Icons.ChartPie, capability: SCHOOL_ACTIONS.VIEW_PROGRESS },
  { name: 'Cohort Data', href: '/cohort-data', icon: Icons.ChartBar, capability: SCHOOL_ACTIONS.REVIEW_APDR_QUEUE },
  { name: 'EHCNA', href: '/ehcna', icon: Icons.ClipboardDocumentCheck, capability: SCHOOL_ACTIONS.GENERATE_EHCNA },
  { name: 'School Settings', href: '/settings', icon: Icons.Cog, capability: SCHOOL_ACTIONS.VIEW_DASHBOARD },
  { name: 'Help & Support', href: '/help', icon: Icons.QuestionMarkCircle, capability: SCHOOL_ACTIONS.VIEW_DASHBOARD },
]

const teams = [
  { id: 1, name: 'System Overview', href: '/help/dashboard', initial: 'S' },
  { id: 2, name: 'Student Management', href: '/help/student-management', initial: 'S' },
  { id: 3, name: 'Curriculum Generation', href: '/help/curriculum-generation', initial: 'C' },
  { id: 4, name: 'Progress Tracking', href: '/help/progress-tracking', initial: 'P' },
  { id: 5, name: 'Term Planning', href: '/help/term-planning', initial: 'T' },
  { id: 6, name: 'Intervention System', href: '/intervention-demo', initial: 'I' },
  { id: 7, name: 'Graduated Response', href: '/help/graduated-response', initial: 'G' },
]

const userNavigation = [
  { name: 'Your profile', href: '/profile' },
  { name: 'Sign out', href: '#', action: 'signout' },
]

const AppLayoutContext = createContext(false);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function AppLayout({ children }) {
  const isNestedLayout = useContext(AppLayoutContext);
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved ? JSON.parse(saved) : false
  })
  const navigate = useNavigate()
  const location = useLocation()
  const { role } = useUserRole({ subscribe: true })
  const { can } = useSchoolCapabilities()
  const { schoolId } = useSchoolId()
  const [headerAvatar, setHeaderAvatar] = useState(null)
  const [globalSearch, setGlobalSearch] = useState('')
  const [searchSubmitting, setSearchSubmitting] = useState(false)

  // Persist sidebar collapse state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed))
  }, [sidebarCollapsed])

  useEffect(() => {
    (async () => {
      try {
        const uid = auth.currentUser?.uid
        if (!uid) { setHeaderAvatar(null); return }
        let url = avatarUrl(auth.currentUser?.email || uid, 64)
        const userSnap = await getDoc(doc(db, 'users', uid))
        if (userSnap.exists()) {
          const u = userSnap.data() || {}
          if (u.staffId) {
            const staffSnap = await getDoc(doc(db, 'staff', u.staffId))
            if (staffSnap.exists()) {
              url = staffAvatar({ id: u.staffId, ...(staffSnap.data() || {}) }, 64)
            }
          } else {
            // Fallback: find staff profile by email (same school via rules)
            const email = auth.currentUser?.email || u.email
            if (email) {
              const q = query(collection(db, 'staff'), where('email', '==', email))
              const qs = await getDocs(q)
              const staffDoc = qs.docs[0]?.data()
              if (staffDoc) {
                url = staffAvatar({ id: qs.docs[0].id, ...staffDoc }, 64)
              }
            }
          }
        }
        setHeaderAvatar(url)
      } catch {
        setHeaderAvatar(null)
      }
    })()
  }, [])

  // Include Staff menu for all roles (rates hidden outside SENCO)
  const navigation = baseNavigation.filter(item => !item.capability || can(item.capability))
  {
    const idx = navigation.findIndex(i => i.href === '/student-organization')
    const staffItem = { name: 'Staff Directory', href: '/staff', icon: Icons.Users }
    if (idx >= 0) navigation.splice(idx + 1, 0, staffItem)
    else navigation.unshift(staffItem)
  }

  // Add Admin: User Approvals for SENCOs only, positioned just above Help & Support
  if (hasRole(role, ROLES.SENCO)) {
    const approvalsItem = { name: 'User Approvals', href: '/admin/user-approvals', icon: Icons.Cog6Tooth }
    const helpIdx = navigation.findIndex(i => i.href === '/help')
    if (helpIdx >= 0) navigation.splice(helpIdx, 0, approvalsItem)
    else navigation.push(approvalsItem)
  }

  // Add Resource Management for Super Admin only
  if (role === ROLES.SUPER_ADMIN) {
    const resourceMgmtItem = { name: 'Resource Management', href: '/admin/resources', icon: Icons.Cog6Tooth }
    const helpIdx = navigation.findIndex(i => i.href === '/help')
    if (helpIdx >= 0) navigation.splice(helpIdx, 0, resourceMgmtItem)
    else navigation.push(resourceMgmtItem)
  }

  // Update navigation current state based on current location
  const updatedNavigation = navigation.map(item => ({
    ...item,
    current: location.pathname === item.href || (item.href !== '/dashboard' && location.pathname.startsWith(`${item.href}/`))
  }))

  const handleLogout = async () => {
    try {
      await signOut(auth)
      navigate('/')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handleUserNavigation = (item) => {
    if (item.action === 'signout') {
      handleLogout()
    } else if (item.href) {
      navigate(item.href)
    }
  }

  if (isNestedLayout) {
    return children
  }

  return (
    <AppLayoutContext.Provider value={true}>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                        <span className="sr-only">Close sidebar</span>
                        <Icons.XMark aria-hidden="true" className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </Transition.Child>

                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center">
                      <img src="/brand/logo-thumbnail-blue.png" alt="SEN-Ed" className="h-8 w-8 rounded-lg mr-2" />
                      <span className="text-xl font-semibold text-primary-600">SEN-Ed</span>
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul className="-mx-2 space-y-1">
                            {updatedNavigation.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  className={classNames(
                                    item.current
                                      ? 'bg-gray-50 text-blue-600'
                                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50',
                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600',
                                      'h-6 w-6 shrink-0'
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li>
                          <div className="text-xs font-semibold leading-6 text-gray-400">Help & Guides</div>
                          <ul className="-mx-2 mt-2 space-y-1">
                            {teams.map((team) => (
                              <li key={team.name}>
                                <Link
                                  to={team.href}
                                  className="text-gray-700 hover:text-blue-600 hover:bg-gray-50 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                                >
                                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-[0.625rem] font-medium text-gray-400 group-hover:border-indigo-600 group-hover:text-blue-600">
                                    {team.initial}
                                  </span>
                                  <span className="truncate">{team.name}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li className="mt-auto">
                  <button
                    onClick={() => navigate('/settings')}
                            className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                          >
                            <Icons.Cog6Tooth
                              className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-blue-600"
                              aria-hidden="true"
                            />
                            Settings
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop - Collapsible */}
        <div className={classNames(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300',
          sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'
        )}>
          <div className={classNames(
            'flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white pb-4 transition-all duration-300',
            sidebarCollapsed ? 'px-3' : 'px-6'
          )}>
            <div className="flex h-16 shrink-0 items-center justify-between">
              <div className="flex items-center">
                <img src="/brand/logo-thumbnail-blue.png" alt="SEN-Ed" className="h-8 w-8 rounded-lg" />
                {!sidebarCollapsed && <span className="text-xl font-semibold text-primary-600 ml-2">SEN-Ed</span>}
              </div>
              {/* Collapse toggle button in header */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? (
                  <Icons.ChevronRight className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Icons.ChevronLeft className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
            <nav className="flex flex-1 flex-col">
              <ul className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul className={classNames(sidebarCollapsed ? '-mx-1' : '-mx-2', 'space-y-1')}>
                    {updatedNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          title={sidebarCollapsed ? item.name : undefined}
                          className={classNames(
                            item.current
                              ? 'bg-gray-50 text-blue-600'
                              : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                            sidebarCollapsed && 'justify-center'
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600',
                              'h-6 w-6 shrink-0'
                            )}
                            aria-hidden="true"
                          />
                          {!sidebarCollapsed && item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                {!sidebarCollapsed && (
                  <li>
                    <div className="text-xs font-semibold leading-6 text-gray-400">Help & Guides</div>
                    <ul className="-mx-2 mt-2 space-y-1">
                      {teams.map((team) => (
                        <li key={team.name}>
                          <Link
                            to={team.href}
                            className="text-gray-700 hover:text-blue-600 hover:bg-gray-50 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                          >
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-[0.625rem] font-medium text-gray-400 group-hover:border-indigo-600 group-hover:text-blue-600">
                              {team.initial}
                            </span>
                            <span className="truncate">{team.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
                <li className="mt-auto">
                  <button
                    onClick={() => navigate('/settings')}
                    title={sidebarCollapsed ? 'Settings' : undefined}
                    className={classNames(
                      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-blue-600 w-full',
                      sidebarCollapsed ? '-mx-1 justify-center' : '-mx-2'
                    )}
                  >
                    <Icons.Cog6Tooth
                      className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-blue-600"
                      aria-hidden="true"
                    />
                    {!sidebarCollapsed && 'Settings'}
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className={classNames(
          'transition-all duration-300',
          sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'
        )}>
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Icons.Bars3 className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <form
                className="relative flex flex-1"
                onSubmit={async (e) => {
                  e.preventDefault()
                  const queryText = globalSearch.trim()
                  if (!queryText) return
                  if (!schoolId) {
                    navigate(`/student-organization?search=${encodeURIComponent(queryText)}`)
                    return
                  }
                  setSearchSubmitting(true)
                  try {
                    const students = await fetchStudentsForSchool(db, schoolId)
                    const match = resolveStudentSearch(students, queryText)
                    if (match?.id) {
                      navigate(`/student/${match.id}`)
                      setGlobalSearch('')
                    } else {
                      navigate(`/student-organization?search=${encodeURIComponent(queryText)}`)
                    }
                  } finally {
                    setSearchSubmitting(false)
                  }
                }}
              >
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <Icons.MagnifyingGlass
                  className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="search-field"
                  className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search students by name or year group"
                  type="search"
                  name="search"
                  value={globalSearch}
                  disabled={searchSubmitting}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                />
              </form>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <Icons.Bell className="h-6 w-6" aria-hidden="true" />
                </button>

                {/* Help Link */}
                <Link
                  to="/help"
                  className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
                  title="Help & Support"
                >
                  <span className="sr-only">Help & Support</span>
                  <Icons.QuestionMarkCircle className="h-6 w-6" aria-hidden="true" />
                </Link>

                {/* Separator */}
                <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" aria-hidden="true" />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <img
                      className="h-8 w-8 rounded-full bg-gray-50"
                      src={headerAvatar || avatarUrl(auth.currentUser?.uid || 'user', 64)}
                      alt=""
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span className="ml-4 text-sm font-semibold leading-6 text-gray-900" aria-hidden="true">
                        {role || 'User'}
                      </span>
                      <Icons.ChevronDown className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      {userNavigation.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <button
                              onClick={() => handleUserNavigation(item)}
                              className={classNames(
                                active ? 'bg-gray-50' : '',
                                'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900'
                              )}
                            >
                              {item.name}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <main>
            <div className="px-4 sm:px-6 lg:px-8 py-6">
              {children}
            </div>
          </main>
        </div>
      </div>
    </AppLayoutContext.Provider>
  )
} 
